import type { Tf2Class, Tf2PlayableTeam } from "./contract"

export const TF2_HUD_RESOURCES = Object.freeze({
  layout: "scripts/HudLayout.res",
  animations: "scripts/HudAnimations_tf.txt",
  clientScheme: "resource/ClientScheme.res",
  playerHealth: "resource/UI/HudPlayerHealth.res",
  playerClass: "resource/UI/HudPlayerClass.res",
  weaponAmmo: "resource/UI/HudAmmoWeapons.res",
  demomanPipes: "resource/UI/HudDemomanPipes.res",
  demomanCharge: "resource/UI/HudDemomanCharge.res",
  weaponSelection: "resource/UI/HudWeaponSelection.res",
  objectiveStatus: "resource/UI/HudObjectiveStatus.res",
  objectiveFlagPanel: "resource/UI/HudObjectiveFlagPanel.res",
  flagStatus: "resource/UI/FlagStatus.res",
  winPanel: "resource/UI/WinPanel.res",
  scoreboard: "resource/UI/Scoreboard.res",
  freezePanel: "resource/UI/FreezePanel_Basic.res",
})

export const TF2_HUD_RESOURCE_REVISIONS = Object.freeze({
  layout: "1f18cb73d9ef54ff79ea208c9996db0655ac731b2ee8e9a82ff63a4b697f400f",
  animations: "cffca69ab872c358d8afb566e78acde022062e87b4fdcfa1779e77dd28ad52ff",
  clientScheme: "1d071b99def0405cbf73d97642a396e6dcbad1a7488f12696ca5dd62893c604c",
  playerHealth: "31fabca97c196eb2cff565b18ff8b3a17aa8806c8e1a980c9376782be7fa4774",
  playerClass: "10181165d10a81821672fd8e104d798e18cf896ca1156cf92df8ce0a07f8c89d",
  weaponAmmo: "a23a98f009dd34ac8c94e7149b1ded56eb9ed66e03d583fcd9c2ab68c3cb7734",
  demomanPipes: "607417c4abf5e469cf15e2d15d45ae5ccc826fe4a6b5f28444241a91a701bb55",
  demomanCharge: "7466e3631f92b5064710658863454bb84a5adb5a77de78eda00b87b01320e533",
  weaponSelection: "7a6f02c7eab4f0befdac5c69082c9334b0a03975738e1fc6d598ba6c91967138",
  objectiveStatus: "b46a3a3d4cbfa57d28d15292036f0de16bfdd928f82034dda4f37617576875ca",
  objectiveFlagPanel: "8affbaac643b77e07e3561497d4f6cbc1abd919d497c92fe0a683015d9adc683",
  flagStatus: "69b56afcf854afe54cd745054f1f720540933a23838bf8f77f34d780091176ca",
  winPanel: "e03fe37c651b1958fedd58b8062a88cc76a7728a6b1d98f21e61af96bbb68089",
  scoreboard: "ed6e7d1619dcfa7423d00cf77c19026d7fe00c6a2bca634bb742d20d870b3e52",
  freezePanel: "aa7eb65149c32af23766daccee2fa6cb45a88ca09e4875008bdf692671f6a15e",
})

export const TF2_SCOREBOARD_CLASS_IMAGES = Object.freeze([
  "../hud/leaderboard_class_scout",
  "../hud/leaderboard_class_sniper",
  "../hud/leaderboard_class_soldier",
  "../hud/leaderboard_class_demo",
  "../hud/leaderboard_class_medic",
  "../hud/leaderboard_class_heavy",
  "../hud/leaderboard_class_pyro",
  "../hud/leaderboard_class_spy",
  "../hud/leaderboard_class_engineer",
] as const)

export const TF2_SCOREBOARD_IMAGES = Object.freeze([
  ...TF2_SCOREBOARD_CLASS_IMAGES.flatMap((image) => [image, `${image}_d`]),
  ...["low", "med", "high", "very_high", "bot_red", "bot_blue"]
    .flatMap((level) => [`../hud/scoreboard_ping_${level}`, `../hud/scoreboard_ping_${level}_d`]),
])

export const TF2_HUD_DYNAMIC_IMAGES = Object.freeze([
  "../hud/class_scoutred", "../hud/class_sniperred", "../hud/class_soldierred", "../hud/class_demored",
  "../hud/class_medicred", "../hud/class_heavyred", "../hud/class_pyrored", "../hud/class_spyred", "../hud/class_engired",
  "../hud/class_scoutblue", "../hud/class_sniperblue", "../hud/class_soldierblue", "../hud/class_demoblue",
  "../hud/class_medicblue", "../hud/class_heavyblue", "../hud/class_pyroblue", "../hud/class_spyblue", "../hud/class_engiblue",
  "../hud/character_red_bg", "../hud/character_blue_bg", "../hud/character_red_bg_clipped", "../hud/character_blue_bg_clipped",
  "../hud/ammo_red_bg", "../hud/ammo_blue_bg", "hud/health_color", "hud/health_dead",
  "../hud/objectives_flagpanel_ico_flag_home",
  "../hud/objectives_flagpanel_ico_flag_dropped",
  "../hud/objectives_flagpanel_ico_flag_moving",
  "../hud/objectives_flagpanel_compass_red",
  "../hud/objectives_flagpanel_compass_blue",
  "../hud/objectives_flagpanel_compass_red_noarrow",
  "../hud/objectives_flagpanel_compass_blue_noarrow",
  "../hud/objectives_flagpanel_carried_red",
  "../hud/objectives_flagpanel_carried_blue",
  "../hud/objectives_timepanel_red_bg",
  "../hud/objectives_timepanel_blue_bg",
])

export const TF2_CONTROL_POINT_IMAGES = Object.freeze([
  "../sprites/obj_icons/icon_obj_white", "../sprites/obj_icons/capture_highlight",
  ...["neutral", "red", "blu"].flatMap(team => [`../sprites/obj_icons/icon_obj_${team}`, `../sprites/obj_icons/icon_obj_${team}_locked`]),
  ...["red", "blu"].flatMap(team => [`../sprites/obj_icons/icon_obj_cap_${team}`, `../sprites/obj_icons/icon_obj_cap_${team}_up`]),
  "progress_bar", "progress_bar_red", "progress_bar_blu",
  "progress_bar_pointer", "progress_bar_pointer_left", "progress_bar_pointer_right", "progress_bar_noCap",
])

export const TF2_CLASS_IMAGES: Readonly<Record<Tf2PlayableTeam, Readonly<Record<Tf2Class, string>>>> =
  Object.freeze({
    2: Object.freeze({
      1: "../hud/class_scoutred",
      2: "../hud/class_sniperred",
      3: "../hud/class_soldierred",
      4: "../hud/class_demored",
      5: "../hud/class_medicred",
      6: "../hud/class_heavyred",
      7: "../hud/class_pyrored",
      8: "../hud/class_spyred",
      9: "../hud/class_engired",
    }),
    3: Object.freeze({
      1: "../hud/class_scoutblue",
      2: "../hud/class_sniperblue",
      3: "../hud/class_soldierblue",
      4: "../hud/class_demoblue",
      5: "../hud/class_medicblue",
      6: "../hud/class_heavyblue",
      7: "../hud/class_pyroblue",
      8: "../hud/class_spyblue",
      9: "../hud/class_engiblue",
    }),
  })

export type Tf2ConditionPanel = Readonly<{
  condition: number
  panel: string
  group: string
  blueImage: string
  redImage: string
}>

export const TF2_GROUPED_CONDITION_PANELS: readonly Tf2ConditionPanel[] = Object.freeze([
  Object.freeze({ condition: 58, panel: "PlayerStatus_MedicUberBulletResistImage", group: "bullet-resist", blueImage: "../HUD/defense_buff_bullet_blue", redImage: "../HUD/defense_buff_bullet_red" }),
  Object.freeze({ condition: 59, panel: "PlayerStatus_MedicUberBlastResistImage", group: "blast-resist", blueImage: "../HUD/defense_buff_explosion_blue", redImage: "../HUD/defense_buff_explosion_red" }),
  Object.freeze({ condition: 60, panel: "PlayerStatus_MedicUberFireResistImage", group: "fire-resist", blueImage: "../HUD/defense_buff_fire_blue", redImage: "../HUD/defense_buff_fire_red" }),
  Object.freeze({ condition: 61, panel: "PlayerStatus_MedicSmallBulletResistImage", group: "bullet-resist", blueImage: "../HUD/defense_buff_bullet_blue", redImage: "../HUD/defense_buff_bullet_red" }),
  Object.freeze({ condition: 62, panel: "PlayerStatus_MedicSmallBlastResistImage", group: "blast-resist", blueImage: "../HUD/defense_buff_explosion_blue", redImage: "../HUD/defense_buff_explosion_red" }),
  Object.freeze({ condition: 63, panel: "PlayerStatus_MedicSmallFireResistImage", group: "fire-resist", blueImage: "../HUD/defense_buff_fire_blue", redImage: "../HUD/defense_buff_fire_red" }),
  Object.freeze({ condition: 16, panel: "PlayerStatus_SoldierOffenseBuff", group: "soldier-offense", blueImage: "../Effects/soldier_buff_offense_blue", redImage: "../Effects/soldier_buff_offense_red" }),
  Object.freeze({ condition: 26, panel: "PlayerStatus_SoldierDefenseBuff", group: "soldier-defense", blueImage: "../Effects/soldier_buff_defense_blue", redImage: "../Effects/soldier_buff_defense_red" }),
  Object.freeze({ condition: 29, panel: "PlayerStatus_SoldierHealOnHitBuff", group: "soldier-heal", blueImage: "../Effects/soldier_buff_healonhit_blue", redImage: "../Effects/soldier_buff_healonhit_red" }),
  Object.freeze({ condition: 90, panel: "PlayerStatus_RuneStrength", group: "rune-strength", blueImage: "../Effects/powerup_strength_hud", redImage: "../Effects/powerup_strength_hud" }),
  Object.freeze({ condition: 91, panel: "PlayerStatus_RuneHaste", group: "rune-haste", blueImage: "../Effects/powerup_haste_hud", redImage: "../Effects/powerup_haste_hud" }),
  Object.freeze({ condition: 92, panel: "PlayerStatus_RuneRegen", group: "rune-regen", blueImage: "../Effects/powerup_regen_hud", redImage: "../Effects/powerup_regen_hud" }),
  Object.freeze({ condition: 93, panel: "PlayerStatus_RuneResist", group: "rune-resist", blueImage: "../Effects/powerup_resist_hud", redImage: "../Effects/powerup_resist_hud" }),
  Object.freeze({ condition: 94, panel: "PlayerStatus_RuneVampire", group: "rune-vampire", blueImage: "../Effects/powerup_vampire_hud", redImage: "../Effects/powerup_vampire_hud" }),
  Object.freeze({ condition: 95, panel: "PlayerStatus_RuneReflect", group: "rune-reflect", blueImage: "../Effects/powerup_reflect_hud", redImage: "../Effects/powerup_reflect_hud" }),
  Object.freeze({ condition: 96, panel: "PlayerStatus_RunePrecision", group: "rune-precision", blueImage: "../Effects/powerup_precision_hud", redImage: "../Effects/powerup_precision_hud" }),
  Object.freeze({ condition: 97, panel: "PlayerStatus_RuneAgility", group: "rune-agility", blueImage: "../Effects/powerup_agility_hud", redImage: "../Effects/powerup_agility_hud" }),
  Object.freeze({ condition: 103, panel: "PlayerStatus_RuneKnockout", group: "rune-knockout", blueImage: "../Effects/powerup_knockout_hud", redImage: "../Effects/powerup_knockout_hud" }),
  Object.freeze({ condition: 109, panel: "PlayerStatus_RuneKing", group: "rune-king", blueImage: "../Effects/powerup_king_hud", redImage: "../Effects/powerup_king_hud" }),
  Object.freeze({ condition: 110, panel: "PlayerStatus_RunePlague", group: "rune-plague", blueImage: "../Effects/powerup_plague_hud", redImage: "../Effects/powerup_plague_hud" }),
  Object.freeze({ condition: 111, panel: "PlayerStatus_RuneSupernova", group: "rune-supernova", blueImage: "../Effects/powerup_supernova_hud", redImage: "../Effects/powerup_supernova_hud" }),
  Object.freeze({ condition: 80, panel: "PlayerStatus_Parachute", group: "parachute", blueImage: "../HUD/hud_parachute_active", redImage: "../HUD/hud_parachute_active" }),
])

export const TF2_INDEPENDENT_CONDITION_PANELS = Object.freeze([
  Object.freeze({ panel: "PlayerStatusBleedImage", conditions: Object.freeze([25]) }),
  Object.freeze({ panel: "PlayerStatusHookBleedImage", conditions: Object.freeze([101]) }),
  Object.freeze({ panel: "PlayerStatusMilkImage", conditions: Object.freeze([27]) }),
  Object.freeze({ panel: "PlayerStatusMarkedForDeathImage", conditions: Object.freeze([30]) }),
  Object.freeze({ panel: "PlayerStatusMarkedForDeathSilentImage", conditions: Object.freeze([48, 119]) }),
  Object.freeze({ panel: "PlayerStatusSlowed", conditions: Object.freeze([15]) }),
  Object.freeze({ panel: "PlayerStatusGasImage", conditions: Object.freeze([123]) }),
])
